import { useCallback, useEffect } from "react";

import { useClientCartOrder } from "@/storage";
import { NuxtCommonCart, NuxtCommonOrder, NuxtStoreCart } from "@/storage/types";
import { useClientLocalStorage } from "@/storage/useClientLocalStorage";

import { useAuth } from "../auth";

interface BotchanGlobal {
  pushCheckout?: (orderStr: string, cartStr: string, token: string) => void;
}

declare global {
  interface Window {
    _bc: BotchanGlobal;
  }
}
/**
 * chatbot経由で、購入した場合に、発火するコールバックをセットするhooks
 * 購入後にchatbotベンダー(slack: side-kicks_chatbot)がwindow._bc.pushCheckoutを実行する仕様であり
 * window._bc.pushCheckoutにcallbackにセットすることで、購入後の後続処理を記述できる
 */
export const useActivateBotchanCallback = () => {
  const [, setCartCart] = useClientLocalStorage<NuxtStoreCart["cart"] | undefined>({
    key: "vuex.cart.cart",
  });
  const { setOrder } = useClientCartOrder();
  const { setAccessToken } = useAuth();
  const callback = useCallback<NonNullable<BotchanGlobal["pushCheckout"]>>(
    async (orderStr, cartStr, token) => {
      await setAccessToken(token);
      setCartCart(JSON.parse(cartStr) as NuxtCommonCart);
      const order = JSON.parse(orderStr);
      if (isOrder(order)) {
        setOrder(order);
      } else {
        if (process.env.NEXT_PUBLIC_APP_ENV === "production") {
          // eslint-disable-next-line no-console
          console.error("Orderの型情報が間違っています。");
        } else {
          throw new Error("Orderの型情報が間違っています。");
        }
      }
      window.location.href = "/checkout/5";
    },
    [setAccessToken, setCartCart, setOrder]
  );
  useEffect(() => {
    if (!window._bc) window._bc = {};
    window._bc.pushCheckout = callback;
    return () => {
      window._bc.pushCheckout = undefined;
    };
  }, [callback]);
};

// 外部からのデータなので、型チェックするが、正確な型は不明なので、この関数で型チェックする
const isOrder = (order: unknown): order is NuxtCommonOrder => {
  if (typeof order === "object" && order !== null) {
    return "id" in order && "shipping_address" in order;
  }
  return false;
};
